import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import Breadcrumb, { BreadcrumbSchema } from '@/components/feature/Breadcrumb';
import ScrollToTopButton from '@/components/base/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { t } = useTranslation();
  const { slug = 'all' } = useParams<{ slug: string }>();
  const cat = categoryMap[slug];
  const [showCount, setShowCount] = useState(6);

  useEffect(() => {
    const catInfo = cat || { name: '全部攻略', description: '浏览所有黄油游戏攻略内容' };
    document.title = `黄油游戏${catInfo.name} - 黄油游戏官方指南`;
  }, [slug, cat]);

  const filteredArticles = slug === 'all' ? articles : articles.filter((a) => a.category === slug);
  const visibleArticles = filteredArticles.slice(0, showCount);
  const hasMore = showCount < filteredArticles.length;

  const categoryName = cat ? cat.name : '全部攻略';

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <ScrollToTopButton />

      <main className="pt-20">
        <section className="px-4 md:px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('category.title'), path: '/category/all' },
                { label: categoryName },
              ]}
            />
            <BreadcrumbSchema
              items={[
                { label: t('breadcrumb.home'), path: '/' },
                { label: t('category.title'), path: '/category/all' },
                { label: categoryName },
              ]}
            />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-3">
                黄油游戏{categoryName}
              </h1>
              <p className="text-foreground-600 max-w-2xl">
                {cat ? cat.description : '浏览所有黄油游戏攻略内容，包括新手入门、角色攻略、隐藏内容、进阶技巧和资源大全'}
              </p>
            </div>

            <div className="mb-8">
              <CategoryTabs activeSlug={slug} />
            </div>

            <div className="mb-8">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-700">{filteredArticles.length}</strong> 篇相关攻略
              </p>
            </div>

            {filteredArticles.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无攻略文章</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {visibleArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
                {hasMore && (
                  <div className="text-center mt-8">
                    <button
                      onClick={() => setShowCount((prev) => prev + 6)}
                      className="px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      加载更多攻略
                      <i className="ri-arrow-down-line ml-2"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}